import { groupBys } from "../constants/quoteOverviewConstants";
import { formatCurrencyForTable, formatPercentageForTable } from "./quoteFunctions";
import { RoundToTwoDecimalPlaces } from "../../../utils/numberUtils";

export const getQuoteOverviewColumns = (groupBySection, groupByColumn1, groupByColumn2) => [
    {
        /* THIS IS THE FIELD USED FOR COLLAPSABLE SECTIONS IN TABLE */
        key: "groupBy1",   
        label: "Build Group",
        dataType: "text",
        groupHeader: true,
        render: (value, row, isExpanded, hasChildren) => {
            return (
                `${groupBys.find(x => x.key === groupBySection)?.label}: ${value || ""}`
            );
        }
    },
    {
        /* THIS IS THE FIELD USED FOR THE FIRST COLUMN IN TABLE */
        key: "groupBy2",
        label: groupBys.find(x => x.key === groupByColumn1)?.label,
        dataType: "text",
        groupColumn: true,
        style: { minWidth: "180px", paddingLeft: "32px" },
        render: (value, row, isExpanded, hasChildren) => {
            return !hasChildren && (
                value || ""
            );
        }
    },
    ...(
        /* THIS IS THE FIELD USED FOR THE FIRST COLUMN IN TABLE (OPTIONAL) */
        groupByColumn2 !== "None" ? [ {
            key: "groupBy3",
            label: groupBys.find(x => x.key === groupByColumn2)?.label,
            dataType: "text",
            groupColumn: true,
            style: { minWidth: "180px" },
            render: (value, row, isExpanded, hasChildren) => {
                return !hasChildren && (
                    value || ""
                );
            }   
        }] : []
    ),     
    {
        key: "totalUpfrontPrice",
        label: "Upfront Sell",
        dataType: "currency",
        showOnTables: ["upfront"],
        style: { borderLeft: "2px solid var(--text-color-muted)" },
        render: (value, row, isExpanded, hasChildren) => {
            return (
                (!isExpanded || !hasChildren) && formatCurrencyForTable(value)
            )
        }
    },
    {
        key: "totalInternalUpfrontCost",
        label: "Internal Upfront Costs",
        dataType: "currency",
        showOnTables: ["upfront"],
        render: (value, row, isExpanded, hasChildren) => {
            return (
                (!isExpanded || !hasChildren) && 
                    formatCurrencyForTable(value)
            )
        }
    },
    {
        key: "totalExternalUpfrontCost",
        label: "External Upfront Costs",
        dataType: "currency",
        showOnTables: ["upfront"],
        render: (value, row, isExpanded, hasChildren) => {
            return (
                (!isExpanded || !hasChildren) && (!isExpanded || !hasChildren) && formatCurrencyForTable(value)
            )
        }
    },
    {
        key: "totalUpfrontCost",
        label: "Upfront Costs",
        dataType: "currency",
        showOnTables: ["upfront"],
        render: (value, row, isExpanded, hasChildren) => {
            return (
                (!isExpanded || !hasChildren) && formatCurrencyForTable(value)
            )
        }
    },
    {
        key: "totalUpfrontMargin",
        label: "Upfront GM",
        dataType: "currency",
        showOnTables: ["upfront"],
        render: (value, row, isExpanded, hasChildren) => {
            return (
                (!isExpanded || !hasChildren) && formatCurrencyForTable(value)
            )
        }
    },
    {
        key: "totalUpfrontMarginPercentage",
        label: "Upfront GM %",
        dataType: "percentage",
        showOnTables: ["upfront"],
        render: (value, row, isExpanded, hasChildren) => {
            return (
                (!isExpanded || !hasChildren) && formatPercentageForTable(value)
            )
        },
        calculateTotal: (itemTotals) => {
            return itemTotals.totalUpfrontPrice > 0 ? RoundToTwoDecimalPlaces((itemTotals.totalUpfrontMargin / itemTotals.totalUpfrontPrice) * 100) : 0;
        }
    },
    {
        key: "totalUpfrontRLEC",
        label: "Upfront RLEC",
        dataType: "currency",
        showOnTables: ["upfront"],
        render: (value, row, isExpanded, hasChildren) => {
            return (
                (!isExpanded || !hasChildren) && formatCurrencyForTable(value)
            )
        }
    },
    {
        key: "totalUpfrontRLECPercentage",
        label: "Upfront RLEC %",
        dataType: "percentage",
        showOnTables: ["upfront"],
        render: (value, row, isExpanded, hasChildren) => {
            return (
                (!isExpanded || !hasChildren) && formatPercentageForTable(value)
            )
        },
        calculateTotal: (itemTotals) => {
            return itemTotals.totalUpfrontPrice > 0 ? RoundToTwoDecimalPlaces((itemTotals.totalUpfrontRLEC / itemTotals.totalUpfrontPrice) * 100) : 0;
        }
    },
    {
        key: "totalAnnualPrice",
        label: "Annual Price",
        dataType: "currency",
        showOnTables: ["annual"],
        style: { borderLeft: "2px solid var(--text-color-muted)" },
        render: (value, row, isExpanded, hasChildren) => {
            return (
                (!isExpanded || !hasChildren) && formatCurrencyForTable(value)
            )
        }
    },
    {
        key: "totalExternalAnnualCost",
        label: "Third Party Annual Costs",
        dataType: "currency",
        showOnTables: ["annual"],
        render: (value, row, isExpanded, hasChildren) => {
            return (
                (!isExpanded || !hasChildren) && (!isExpanded || !hasChildren) && formatCurrencyForTable(value)
            )
        }
    },
    {
        key: "totalAnnualRLEC",
        label: "Annual RLEC",
        dataType: "currency",
        showOnTables: ["annual"],
        render: (value, row, isExpanded, hasChildren) => {
            return (
                (!isExpanded || !hasChildren) && formatCurrencyForTable(value)
            )
        }
    },
    {
        key: "totalAnnualRLECPercentage",
        label: "Annual RLEC %",
        dataType: "percentage",
        showOnTables: ["annual"],
        render: (value, row, isExpanded, hasChildren) => {
            return (
                (!isExpanded || !hasChildren) && formatPercentageForTable(value)
            )
        },
        calculateTotal: (itemTotals) => {
            return itemTotals.totalAnnualPrice > 0 ? RoundToTwoDecimalPlaces((itemTotals.totalAnnualRLEC / itemTotals.totalAnnualPrice) * 100) : 0;
        }
    },
    {
        key: "totalInternalAnnualCost",
        label: "Internal Annual Costs",
        dataType: "currency",
        showOnTables: ["annual"],
        render: (value, row, isExpanded, hasChildren) => {
            return (
                (!isExpanded || !hasChildren) && formatCurrencyForTable(value)
            )
        }
    },
    {
        key: "totalAnnualMargin",
        label: "Annual GM",
        dataType: "currency",
        showOnTables: ["annual"],
        render: (value, row, isExpanded, hasChildren) => {
            return (
                (!isExpanded || !hasChildren) && formatCurrencyForTable(value)
            )
        }
    },
    {
        key: "totalAnnualMarginPercentage",
        label: "Annual GM %",
        dataType: "percentage",
        showOnTables: ["annual"],
        render: (value, row, isExpanded, hasChildren) => {
            return (
                (!isExpanded || !hasChildren) && formatPercentageForTable(value)
            )
        },
        calculateTotal: (itemTotals) => {
            return itemTotals.totalAnnualPrice > 0 ? RoundToTwoDecimalPlaces((itemTotals.totalAnnualMargin / itemTotals.totalAnnualPrice) * 100) : 0;
        }
    },      
    {
        key: "totalRecurringPrice",
        label: "Recurring Sell",
        dataType: "currency",
        showOnTables: ["recurring"],
        style: { borderLeft: "2px solid var(--text-color-muted)" },
        render: (value, row, isExpanded, hasChildren) => {
            return (
                (!isExpanded || !hasChildren) && formatCurrencyForTable(value)
            )
        }
    },
    {
        key: "totalInternalRecurringCost",
        label: "Internal Recurring Costs",
        dataType: "currency",
        showOnTables: ["recurring"],
        render: (value, row, isExpanded, hasChildren) => {
            return (
                (!isExpanded || !hasChildren) && formatCurrencyForTable(value)
            )
        },
    },
    {
        key: "totalExternalRecurringCost",
        label: "External Recurring Costs",
        dataType: "currency",
        showOnTables: ["recurring"],
        render: (value, row, isExpanded, hasChildren) => {
            return (
                (!isExpanded || !hasChildren) && formatCurrencyForTable(value)
            )
        }
    },
    {
        key: "totalRecurringCost",
        label: "Recurring Costs",
        dataType: "currency",
        showOnTables: ["recurring"],
        render: (value, row, isExpanded, hasChildren) => {
            return (
                (!isExpanded || !hasChildren) && formatCurrencyForTable(value)
            )
        }
    },
    {
        key: "totalRecurringMargin",
        label: "Recurring GM",
        dataType: "currency",
        showOnTables: ["recurring"],
        render: (value, row, isExpanded, hasChildren) => {
            return (
                (!isExpanded || !hasChildren) && formatCurrencyForTable(value)
            )
        }
    },
    {
        key: "totalRecurringMarginPercentage",
        label: "Recurring GM %",
        dataType: "percentage",
        showOnTables: ["recurring"],
        render: (value, row, isExpanded, hasChildren) => {
            return (
                (!isExpanded || !hasChildren) && formatPercentageForTable(value)
            )
        },
        calculateTotal: (itemTotals) => {
            return itemTotals.totalRecurringPrice > 0 ? RoundToTwoDecimalPlaces((itemTotals.totalRecurringMargin / itemTotals.totalRecurringPrice) * 100) : 0;
        }
    },
    {
        key: "totalRecurringRLEC",
        label: "Recurring RLEC",
        dataType: "currency",
        showOnTables: ["recurring"],
        render: (value, row, isExpanded, hasChildren) => {        
            return (
                (!isExpanded || !hasChildren) && formatCurrencyForTable(value)
            )
        }
    },
    {
        key: "totalRecurringRLECPercentage",
        label: "Recurring RLEC %",
        dataType: "percentage",
        showOnTables: ["recurring"],
        render: (value, row, isExpanded, hasChildren) => {
            return (
                (!isExpanded || !hasChildren) && formatPercentageForTable(value)
            )
        },
        calculateTotal: (itemTotals) => {
            return itemTotals.totalRecurringPrice > 0 ? RoundToTwoDecimalPlaces((itemTotals.totalRecurringRLEC / itemTotals.totalRecurringPrice) * 100) : 0;
        }
    },
    {
        key: "totalContractPrice",
        label: "Contract Price",
        dataType: "currency",
        showOnTables: ["contract"],
        style: { borderLeft: "2px solid var(--text-color-muted)" },
        render: (value, row, isExpanded, hasChildren) => {
            return (
                (!isExpanded || !hasChildren) && formatCurrencyForTable(value)
            )
        }
    },
    {
        key: "totalExternalContractCost",
        label: "Third Party Contract Costs",
        dataType: "currency",
        showOnTables: ["contract"],
        render: (value, row, isExpanded, hasChildren) => {
            return (
                (!isExpanded || !hasChildren) && (!isExpanded || !hasChildren) && formatCurrencyForTable(value)
            )
        }
    },
    {
        key: "totalContractRLEC",
        label: "Contract RLEC",
        dataType: "currency",
        showOnTables: ["contract"],
        render: (value, row, isExpanded, hasChildren) => {
            return (
                (!isExpanded || !hasChildren) && formatCurrencyForTable(value)
            )
        }
    },
    {
        key: "totalContractRLECPercentage",
        label: "Contract RLEC %",
        dataType: "percentage",
        showOnTables: ["contract"],
        render: (value, row, isExpanded, hasChildren) => {
            return (
                (!isExpanded || !hasChildren) && formatPercentageForTable(value)
            )
        },
        calculateTotal: (itemTotals) => {
            return itemTotals.totalContractPrice > 0 ? RoundToTwoDecimalPlaces((itemTotals.totalContractRLEC / itemTotals.totalContractPrice) * 100) : 0;
        }
    },
    {
        key: "totalInternalContractCost",
        label: "Internal Contract Costs",
        dataType: "currency",
        showOnTables: ["contract"],
        render: (value, row, isExpanded, hasChildren) => {
            return (
                (!isExpanded || !hasChildren) && formatCurrencyForTable(value)
            )
        }
    },
    {
        key: "totalContractMargin",
        label: "Contract GM",
        dataType: "currency",
        showOnTables: ["contract"],
        render: (value, row, isExpanded, hasChildren) => {
            return (
                (!isExpanded || !hasChildren) && formatCurrencyForTable(value)
            )
        }
    },
    {
        key: "totalContractMarginPercentage",
        label: "Contract GM %",
        dataType: "percentage",
        showOnTables: ["contract"],
        render: (value, row, isExpanded, hasChildren) => {
            return (
                (!isExpanded || !hasChildren) && formatPercentageForTable(value)
            )
        },
        calculateTotal: (itemTotals) => {
            return itemTotals.totalContractPrice > 0 ? RoundToTwoDecimalPlaces((itemTotals.totalContractMargin / itemTotals.totalContractPrice) * 100) : 0;
        }
    },
].map(col => ({
    // Add default styles for all columns here
    ...col,
    style: { textAlign: col.groupColumn || col.groupHeader ? "left" : "right", width: "100px", ...col.style },
    align: col.groupColumn || col.groupHeader ? "left" : "right"
})) 